import { useState } from 'react';

import { AskMode } from '@/entities/ask-syncly';
import { fetchTemplateQuestions } from '@/entities/ask-syncly/api';
import { usePolling } from '@/shared/hooks/use-polling';
import { useWorkspace } from '@/shared/hooks/use-workspace';

type TemplateQuestionsResponse = {
  templates: string[];
  status: 'completed' | 'running';
};

export function useTemplateQuestions(
  workspaceId: string,
  queryId: string,
  mode: AskMode,
): {
  templateQuestions: string[] | null;
  loading: boolean;
} {
  const { workspace } = useWorkspace();
  const [templateQuestions, setTemplateQuestions] = useState<string[] | null>(null);

  const { loading } = usePolling<TemplateQuestionsResponse>({
    enabled: mode === AskMode.GENERAL,
    interval: 20000, // 20 seconds
    queryKey: `template-questions-${workspaceId}-${queryId}`,
    fetcher: async ({ signal }) => {
      try {
        const data = await fetchTemplateQuestions(workspaceId, queryId, signal);
        return {
          templates: data.questions,
          status: data.status,
        };
      } catch (error) {
        console.error('Failed to fetch template questions:', error);
        return {
          templates: [],
          status: 'running',
        };
      }
    },
    getCompleteCondition: (data) => data.status === 'completed',
    onComplete(data) {
      setTemplateQuestions(data.templates);
    },
  });

  // Content mode인 경우 static template 반환
  if (mode === AskMode.CONTENT) {
    if (workspace.language === 'ko')
      return {
        templateQuestions: [
          '상위 10개 게시물에서 가장 자주 나타나는 콘텐츠 포맷 유형은 무엇인가요?',
          '높은 참여도를 기록한 영상들에서 공통적으로 나타나는 훅(Hook) 구조는 무엇인가요?',
          '상위 성과 콘텐츠에서 주로 사용되는 스토리텔링 구조는 무엇인가요?',
        ],
        loading: false,
      };
    return {
      templateQuestions: [
        'Across the top 10 posts, what format archetypes appear most frequently?',
        'What hook mechanics are shared across high-engagement videos?',
        'Which narrative structures dominate among the top performers?',
      ],
      loading: false,
    };
  }

  return {
    templateQuestions,
    loading,
  };
}
